import { useEffect, useRef, useState } from 'react'
import { RefreshCcw, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  invalidateSyncedData,
  isFinishedSync,
  useLatestSyncStatus,
  useSyncInventory,
  useSyncStatus,
} from '@/hooks/useSyncInventory'

// One toast for every mounted button: two buttons on screen, or a remount
// after navigating away, update the same toast instead of stacking copies.
export const SYNC_TOAST_ID = 'shopify-sync'

export function SyncShopifyButton({ compact = false }: { compact?: boolean }) {
  const qc = useQueryClient()
  const sync = useSyncInventory()
  // The sync this button started (stays set after it finishes).
  const [started, setStarted] = useState<{ logId: number; mode: string } | null>(null)
  const loadingShownForRef = useRef<number | null>(null)
  const announcedLogIdRef = useRef<number | null>(null)
  const pollingRef = useRef(false)

  // Server's latest row, read on mount: a sync started before a page change,
  // from another button or tab, or by cron is picked up and watched too.
  // Only a NEWER running row overrides ours, so a stale cached "in_progress"
  // row for a sync we already saw finish doesn't restart polling.
  const { data: latest } = useLatestSyncStatus()
  const running = latest?.status === 'in_progress' && latest.log_id != null ? latest : null
  const logId =
    running && (started == null || running.log_id! > started.logId) ? running.log_id! : started?.logId ?? null
  const mode = (running?.log_id === logId ? running?.mode : started?.mode) ?? 'sync'

  // Polling stops by itself once the row is success/failed (see the hook).
  const { data: status } = useSyncStatus(logId, logId != null)
  const finished = status?.log_id === logId && isFinishedSync(status)
  const polling = logId != null && !finished

  useEffect(() => {
    pollingRef.current = polling
    if (!polling || loadingShownForRef.current === logId) return
    loadingShownForRef.current = logId
    toast.loading(`Syncing Shopify (${mode})…`, { id: SYNC_TOAST_ID })
  }, [polling, logId, mode])

  // Leaving mid-sync: drop the loading toast; the next mount resumes polling.
  useEffect(() => () => {
    if (pollingRef.current) toast.dismiss(SYNC_TOAST_ID)
  }, [])

  useEffect(() => {
    if (!finished || !status || announcedLogIdRef.current === logId) return
    announcedLogIdRef.current = logId
    if (status.status === 'success') {
      if (status.error_message?.startsWith('WARNING:')) {
        const n = status.error_message.replace(/^WARNING:\s*/, '').split('; ').length
        toast.warning(
          `Sync complete (${status.mode}) with ${n} warning${n === 1 ? '' : 's'} — see Admin → Data Health`,
          { id: SYNC_TOAST_ID },
        )
      } else {
        toast.success(`Sync complete (${status.mode}) — ${status.duration_seconds?.toFixed(1) ?? '?'}s`, {
          id: SYNC_TOAST_ID,
        })
      }
      invalidateSyncedData(qc)
    } else {
      toast.error(`Sync failed: ${status.error_message ?? 'unknown error'}`, { id: SYNC_TOAST_ID })
    }
    qc.invalidateQueries({ queryKey: ['sync-status', null] })
  }, [finished, status, logId, qc])

  function trigger(mode: 'full' | 'delta') {
    if (mode === 'full') {
      if (!window.confirm('Full re-sync takes ~6 minutes and will overwrite the inventory cache. Continue?')) return
    }
    sync.mutate(mode, {
      onSuccess: (resp) => setStarted({ logId: resp.log_id, mode }),
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Sync failed'
        toast.error(msg, { id: SYNC_TOAST_ID })
        // "Already in progress": re-read the latest row so we pick it up and spin.
        qc.invalidateQueries({ queryKey: ['sync-status', null] })
      },
    })
  }

  const disabled = sync.isPending || polling
  const label = compact ? 'Sync' : 'Sync Shopify'

  return (
    <div className="flex items-stretch">
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        disabled={disabled}
        onClick={() => trigger('delta')}
        data-testid="sync-shopify"
      >
        <RefreshCcw className={`mr-1 h-3 w-3 ${disabled ? 'animate-spin' : ''}`} />
        {label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 px-1 border-l-0" disabled={disabled} aria-label="Sync options">
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => trigger('full')}>Full re-sync</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
